/******/ (() => { // webpackBootstrap
var __webpack_exports__ = {};
/* Accessibility (WCAG 2.2 AA, Oct 2026): button semantics, aria-expanded,
   aria-controls / region relationships, Enter + Space support, and collapsed
   panels removed from the tab order and accessibility tree. */
let collapsibleUid = 0;

class CollapsibleButton extends HTMLElement {
  constructor() {
    super();
    this.THROTTLE_INTERVAL = 100;
    this.bottomGapHeight = 15;
    this.contentBlockheight = () => this.nextElementSibling.querySelector('.content').offsetHeight + this.bottomGapHeight + 'px';
  }

  connectedCallback() {
    this.init();
  }

  init() {
    window.wetheme.webcomponentRegistry.register({key: 'collapsible-button'});
    this.collapsibleContent = this.parentElement.querySelector('.collapsible-content');

    // Button semantics (also server-rendered in snippets/collapsible-tab.liquid)
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

    if(this.nextElementSibling && this.nextElementSibling.classList.contains('collapsible-content') && this.parentElement.classList.contains('collapsible-tab')) {

      this.linkPanel(this.nextElementSibling);

      // Set initial height based on state
      const isOpen = this.parentElement.classList.contains('open');
      isOpen ? this.nextElementSibling.style.height = this.contentBlockheight() : this.nextElementSibling.style.height = '0px';
      this.updateState(isOpen);

      // Handle click
      this.addEventListener('click', this.toggle);

      // Handle Enter and Space keys (native button behaviour)
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.toggle();
        } else if (e.key === ' ' || e.key === 'Spacebar') {
          // Prevent page scroll; activate on keyup like a native button
          e.preventDefault();
        }
      });
      this.addEventListener('keyup', (e) => {
        if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.toggle();
        }
      });

      // Handle window resize
      window.addEventListener('resize', this.handleResize.bind(this));

      // Handle collapsible tab content size change
      this.nextElementSibling.addEventListener('click', this.resetHeight.bind(this));

      // Keep the open panel's height in sync when its content grows, e.g. with
      // browser text zoom or user text-spacing styles (WCAG 1.4.4, 1.4.12)
      const inner = this.nextElementSibling.querySelector('.content');
      if (inner && 'ResizeObserver' in window) {
        new ResizeObserver(() => this.resetHeight()).observe(inner);
      }
    } else {
      // Nothing to control: don't expose an inert control as a button
      this.removeAttribute('aria-expanded');
    }
  }

  linkPanel(panel) {
    collapsibleUid += 1;
    const base = 'Collapsible-' + collapsibleUid;
    if (!this.id) this.id = base + '-button';
    if (!panel.id) panel.id = base + '-panel';
    this.setAttribute('aria-controls', panel.id);
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-labelledby', this.id);
  }

  updateState(isOpen) {
    this.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    const panel = this.nextElementSibling;
    if (!panel) return;
    if (isOpen) {
      panel.removeAttribute('inert');
      panel.removeAttribute('aria-hidden');
    } else {
      panel.setAttribute('inert', '');
      panel.setAttribute('aria-hidden', 'true');
    }
  }

  handleResize() {
    this.throttle(this.resetHeight, this.THROTTLE_INTERVAL);
  }

  resetHeight() {
    if(!this.parentElement.classList.contains('open')) return;
    this.setTransitionHeight(this.nextElementSibling, false);
  }

  toggle() {
    this.parentElement.classList.contains('open') ? this.hideDetails() : this.showDetails();
  }

  showDetails() {
    this.setTransitionHeight(this.nextElementSibling, false)
    this.parentElement.classList.add('open');
    this.updateState(true);

    if (this.collapsibleContent) {
      this.collapsibleContent.addEventListener('transitionend', this.transitionEndHandler.bind(this), {once: true});
    }
  }

  hideDetails() {
    this.setTransitionHeight(this.nextElementSibling, true)
    this.parentElement.classList.remove('open');
    this.updateState(false);

    if (this.collapsibleContent) {
      this.collapsibleContent.style.overflow = 'hidden';
    }
  }

  setTransitionHeight(container, isOpen) {
    if(isOpen) {
      container.style.height = '0px';
    } else {
      container.style.height = this.contentBlockheight();
    }
  }

  transitionEndHandler(event) {
    // Check if the transitionend event is for the height property
    if (event.propertyName === 'height') {
      this.collapsibleContent.style.overflow = 'visible';
    }
  }

  throttle (callback, time) {
    if (this.throttleTimer) return;
    this.throttleTimer = true;
    setTimeout(() => {
      callback.call(this);
      this.throttleTimer = false;
    }, time);
  }

}

// Only define the custom element if it doesn't already exist
if (!customElements.get('collapsible-button')) customElements.define('collapsible-button', CollapsibleButton);

/******/ })()
;
